import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart2, Globe, Bot, Shield, Edit3, Trash2,
  Plus, Eye, TrendingUp, AlertTriangle, Copy,
  ExternalLink, MoreVertical, Pause, Play, X,
  ChevronRight, Activity, Users, Clock
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/useTranslation';
import { mockAnalyticsDaily } from '../services/mockData';
import { auth, firestoreService, isFirebaseConfigured } from '../services/firebase';
import PublishPayment from '../components/PublishPayment';
import type { Site } from '../types';
import { clsx } from 'clsx';

const DAY_MS = 24 * 60 * 60 * 1000;

function daysLeft(paidUntil?: Date | null): number | null {
  if (!paidUntil) return null;
  return Math.ceil((paidUntil.getTime() - Date.now()) / DAY_MS);
}

// ─── Status Badge ────────────────────────────────────────────────────
function StatusBadge({ status, t }: { status: Site['status']; t: (k: any) => string }) {
  const config = {
    published: { label: t('dash.live'), class: 'badge-green', dot: 'status-green' },
    draft: { label: t('dash.draft'), class: 'badge-gray', dot: 'status-gray' },
    paused: { label: t('dash.paused'), class: 'badge-yellow', dot: 'status-yellow' },
    expired: { label: t('dash.expired'), class: 'badge-red', dot: 'status-red' },
  };
  const c = config[status];
  return (
    <span className={clsx('badge', c.class)}>
      <span className={clsx(c.dot, 'mr-1.5')} />
      {c.label}
    </span>
  );
}

// ─── Security Score Bar ───────────────────────────────────────────────
function SecurityScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-blue-500' : score >= 30 ? 'bg-yellow-500' : 'bg-red-500';
  const textColor = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-blue-600' : score >= 30 ? 'text-yellow-600' : 'text-red-600';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-surface-200 rounded-full h-1.5">
        <div className={clsx('h-1.5 rounded-full transition-all', color)} style={{ width: `${score}%` }} />
      </div>
      <span className={clsx('text-xs font-bold', textColor)}>{score}</span>
    </div>
  );
}

// ─── Mini Analytics Chart ─────────────────────────────────────────────
function MiniChart({ data }: { data: typeof mockAnalyticsDaily }) {
  const max = Math.max(...data.map((d) => d.views));
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-surface-900 text-white text-xs rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {d.views} views
          </div>
          <div
            className="w-full bg-telegram/20 hover:bg-telegram/60 rounded-sm transition-all cursor-pointer"
            style={{ height: `${Math.max(4, (d.views / max) * 56)}px` }}
          />
          <div className="text-xs text-surface-400 hidden sm:block">{d.date.split(' ')[0]}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Site Card ────────────────────────────────────────────────────────
function SiteCard({ site, onDelete, t }: { site: Site; onDelete: (id: string) => void; t: (k: any) => string }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card card-hover"
    >
      <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <StatusBadge status={site.status} t={t} />
              {site.templateName && (
                <span className="badge-gray text-xs">{site.templateName}</span>
              )}
            </div>
            <h3 className="font-bold text-surface-900 text-lg truncate">{site.siteName}</h3>
            {site.status === 'published' && site.publicUrl && (
              <div className="flex items-center gap-1.5 mt-1">
                <Globe size={12} className="text-telegram" />
                <span className="text-telegram text-sm truncate">{site.publicUrl.replace('https://', '')}</span>
                <button onClick={() => handleCopy(site.publicUrl)} className="p-1 hover:bg-surface-100 rounded flex-shrink-0">
                  {copied ? <span className="text-green-500 text-xs">✓</span> : <Copy size={12} className="text-surface-400" />}
                </button>
              </div>
            )}
            {site.status !== 'draft' && (() => {
              const d = daysLeft(site.paidUntil);
              if (d === null || d <= 0) {
                return <div className="text-xs text-red-600 mt-1">Langganan tamat — perlu diperbaharui</div>;
              }
              return (
                <div className={clsx('text-xs mt-1', d <= 5 ? 'text-amber-600 font-medium' : 'text-surface-500')}>
                  Aktif sehingga {site.paidUntil!.toLocaleDateString('ms-MY')} ({d} hari lagi)
                </div>
              );
            })()}
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg hover:bg-surface-100 text-surface-500"
            >
              <MoreVertical size={16} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-10 bg-white rounded-xl shadow-card-hover border border-surface-200 py-1.5 z-20 min-w-[160px]"
                >
                  {site.status === 'published' ? (
                    <button className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 hover:bg-surface-50">
                      <Pause size={14} /> {t('dash.pause_site')}
                    </button>
                  ) : site.status === 'paused' ? (
                    <button className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 hover:bg-surface-50">
                      <Play size={14} /> {t('dash.resume_site')}
                    </button>
                  ) : null}
                  {site.publicUrl && (
                    <a
                      href={site.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 hover:bg-surface-50"
                    >
                      <ExternalLink size={14} /> {t('dash.open_site')}
                    </a>
                  )}
                  <button
                    onClick={() => { setMenuOpen(false); setDeleteConfirm(true); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> {t('dash.delete_site')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Stats */}
        {site.status === 'published' && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-surface-50 rounded-lg p-3 text-center">
              <div className="text-xl font-extrabold text-surface-900">{site.analytics.totalViews.toLocaleString()}</div>
              <div className="text-xs text-surface-500 flex items-center justify-center gap-1">
                <Eye size={10} /> {t('dash.views')}
              </div>
            </div>
            <div className="bg-surface-50 rounded-lg p-3 text-center">
              <div className="text-xl font-extrabold text-surface-900">{site.analytics.uniqueVisitors.toLocaleString()}</div>
              <div className="text-xs text-surface-500 flex items-center justify-center gap-1">
                <Users size={10} /> {t('dash.visitors')}
              </div>
            </div>
            <div className="bg-surface-50 rounded-lg p-3 text-center">
              <div className="text-xl font-extrabold text-surface-900">{site.security.securityScore}</div>
              <div className="text-xs text-surface-500 flex items-center justify-center gap-1">
                <Shield size={10} /> {t('dash.score')}
              </div>
            </div>
          </div>
        )}

        {/* Security Score */}
        {site.status === 'published' && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-surface-500 mb-1.5">
              <span className="flex items-center gap-1"><Shield size={10} /> {t('dash.security')}</span>
            </div>
            <SecurityScoreBar score={site.security.securityScore} />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {site.status === 'draft' ? (
            <>
              <button className="btn-primary btn-sm flex-1">
                <Edit3 size={14} />
                {t('dash.continue_edit')}
              </button>
              <button className="btn-outline btn-sm">
                <Eye size={14} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/security')}
                className="btn-outline btn-sm flex-1"
              >
                <Shield size={14} />
                {t('dash.security')}
              </button>
              <button className="btn-outline btn-sm flex-1">
                <BarChart2 size={14} />
                {t('dash.analytics_btn')}
              </button>
              <button className="btn-outline btn-sm">
                <Edit3 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
            onClick={() => setDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={24} className="text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-surface-900 mb-2">{t('dash.delete_title')}</h3>
                <p className="text-surface-500 text-sm mb-6">
                  <strong>"{site.siteName}"</strong> {t('dash.delete_sub')}
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteConfirm(false)} className="btn-outline btn-md flex-1">
                    {t('dash.cancel')}
                  </button>
                  <button
                    onClick={() => { onDelete(site.siteId); setDeleteConfirm(false); }}
                    className="btn-danger btn-md flex-1"
                  >
                    {t('dash.delete')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { mySites, setMySites, user, showToast } = useAppStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'sites' | 'analytics'>('sites');
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [renewSiteId, setRenewSiteId] = useState<string | null>(null);

  const reloadSites = async () => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    try {
      setMySites(await firestoreService.getUserSites(uid));
    } catch {
      /* senarai akan dimuatkan pada lawatan seterusnya */
    }
  };

  const dueSites = mySites
    .filter((s) => s.status === 'published' || s.status === 'expired')
    .filter((s) => {
      const d = daysLeft(s.paidUntil);
      return d === null || d <= 5;
    });

  const handleDelete = (siteId: string) => {
    setMySites(mySites.filter((s) => s.siteId !== siteId));
    showToast(t('dash.deleted'), 'success');
  };

  const totalViews = mySites.reduce((s, site) => s + site.analytics.totalViews, 0);
  const totalVisitors = mySites.reduce((s, site) => s + site.analytics.uniqueVisitors, 0);
  const publishedCount = mySites.filter((s) => s.status === 'published').length;
  const avgSecurity = mySites.length > 0
    ? Math.round(mySites.reduce((s, site) => s + site.security.securityScore, 0) / mySites.length)
    : 0;

  const selectedSite = mySites.find((s) => s.siteId === selectedSiteId) || mySites[0];

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <BarChart2 size={28} className="text-telegram" />
                <h1 className="text-3xl font-extrabold text-surface-900">{t('dash.title')}</h1>
              </div>
              <p className="text-surface-500">{t('dash.welcome')}, {user?.displayName || 'User'}! 👋</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isFirebaseConfigured && user?.role === 'admin' && (
                <Link to="/admin" state={{ fromDashboard: true }} className="btn-outline btn-md">
                  <Shield size={16} />
                  Admin
                </Link>
              )}
              <Link to="/gallery" className="btn-primary btn-md">
                <Plus size={16} />
                {t('dash.new_site')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {dueSites.length > 0 && (
          <div className="card p-5 mb-8 border-amber-200 bg-amber-50">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-amber-800 mb-1">Peringatan pembaharuan</div>
                <p className="text-amber-700 text-sm mb-3">
                  Laman berikut hampir tamat atau sudah tamat tempoh. Sila perbaharui untuk
                  kekal online.
                </p>
                <div className="space-y-2">
                  {dueSites.map((s) => {
                    const d = daysLeft(s.paidUntil);
                    return (
                      <div
                        key={s.siteId}
                        className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2"
                      >
                        <span className="text-sm">
                          <b>{s.siteName}</b>{" "}
                          <span className="text-surface-500">
                            {d === null || d <= 0 ? "(tamat)" : `(${d} hari lagi)`}
                          </span>
                        </span>
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => setRenewSiteId(s.siteId)}
                        >
                          Perbaharui
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Globe, label: t('dash.sites_live'), value: publishedCount, sub: `${mySites.length} ${t('dash.total')}`, color: 'text-telegram', bg: 'bg-telegram/10' },
            { icon: Eye, label: t('dash.total_views'), value: totalViews.toLocaleString(), sub: t('dash.all_sites'), color: 'text-blue-600', bg: 'bg-blue-50' },
            { icon: Users, label: t('dash.unique_visitors'), value: totalVisitors.toLocaleString(), sub: t('dash.all_sites'), color: 'text-purple-600', bg: 'bg-purple-50' },
            { icon: Shield, label: t('dash.avg_security'), value: `${avgSecurity}/100`, sub: t('dash.security_score'), color: 'text-green-600', bg: 'bg-green-50' },
          ].map((stat) => (
            <div key={stat.label} className="card p-5">
              <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center mb-3', stat.bg)}>
                <stat.icon size={20} className={stat.color} />
              </div>
              <div className="text-2xl font-extrabold text-surface-900">{stat.value}</div>
              <div className="text-sm font-medium text-surface-700">{stat.label}</div>
              <div className="text-xs text-surface-400">{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-surface-200 p-1 mb-8 w-fit">
          {[
            { id: 'sites', label: t('dash.my_sites'), count: mySites.length },
            { id: 'analytics', label: t('dash.analytics') },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-telegram text-white shadow-glow'
                  : 'text-surface-600 hover:bg-surface-100'
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={clsx(
                  'badge text-xs',
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-surface-100 text-surface-600'
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ─── Sites Tab ─────────────────────────────────────────── */}
        {activeTab === 'sites' && (
          <div>
            {mySites.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-6">🌐</div>
                <h3 className="text-2xl font-bold text-surface-900 mb-3">{t('dash.empty_title')}</h3>
                <p className="text-surface-500 mb-8 max-w-sm mx-auto">{t('dash.empty_sub')}</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/gallery" className="btn-primary btn-lg">
                    <Plus size={18} />
                    {t('dash.build_first')}
                  </Link>
                  <Link to="/import" className="btn-outline btn-lg">
                    {t('dash.import_html')}
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {mySites.map((site) => (
                    <SiteCard key={site.siteId} site={site} onDelete={handleDelete} t={t} />
                  ))}
                  {/* Add New Card */}
                  <Link
                    to="/gallery"
                    className="card border-2 border-dashed border-surface-300 hover:border-telegram hover:bg-telegram/5 transition-all flex flex-col items-center justify-center p-8 text-center min-h-[200px] group"
                  >
                    <div className="w-14 h-14 bg-surface-100 group-hover:bg-telegram/10 rounded-full flex items-center justify-center mb-3 transition-colors">
                      <Plus size={24} className="text-surface-400 group-hover:text-telegram transition-colors" />
                    </div>
                    <div className="font-semibold text-surface-600 group-hover:text-telegram transition-colors">
                      {t('dash.build_new')}
                    </div>
                    <div className="text-surface-400 text-sm mt-1">
                      {mySites.filter(s => s.status !== 'draft').length} / {user?.maxSites || 3} {t('dash.used')}
                    </div>
                  </Link>
                </div>
              </>
            )}
          </div>
        )}

        {/* ─── Analytics Tab ─────────────────────────────────────── */}
        {activeTab === 'analytics' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Site Selector */}
            <div className="lg:col-span-1 space-y-3">
              <h3 className="font-bold text-surface-900 text-sm uppercase tracking-wide text-surface-500">{t('dash.choose_site')}</h3>
              {mySites.filter(s => s.status === 'published').map((site) => (
                <button
                  key={site.siteId}
                  onClick={() => setSelectedSiteId(site.siteId)}
                  className={clsx(
                    'w-full text-left p-4 rounded-xl border transition-all',
                    (selectedSiteId === site.siteId || (!selectedSiteId && site === mySites[0]))
                      ? 'border-telegram bg-telegram/5 shadow-glow'
                      : 'border-surface-200 bg-white hover:border-telegram/50'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-surface-900 text-sm">{site.siteName}</div>
                      <div className="text-telegram text-xs mt-0.5 truncate">{site.publicUrl.replace('https://', '')}</div>
                    </div>
                    <ChevronRight size={16} className={clsx(
                      'transition-colors',
                      selectedSiteId === site.siteId ? 'text-telegram' : 'text-surface-300'
                    )} />
                  </div>
                </button>
              ))}
            </div>

            {/* Analytics Detail */}
            <div className="lg:col-span-2 space-y-6">
              {selectedSite && (
                <>
                  {/* Chart Card */}
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-bold text-surface-900">{t('dash.views_7d')}</h3>
                        <p className="text-surface-500 text-sm">{selectedSite.siteName}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-surface-900">
                          {mockAnalyticsDaily.reduce((s, d) => s + d.views, 0).toLocaleString()}
                        </div>
                        <div className="text-green-600 text-sm flex items-center gap-1">
                          <TrendingUp size={14} />
                          +23% {t('dash.this_week')}
                        </div>
                      </div>
                    </div>
                    <MiniChart data={mockAnalyticsDaily} />
                    <div className="flex justify-between text-xs text-surface-400 mt-2">
                      <span>19 Ogs</span>
                      <span>25 Ogs</span>
                    </div>
                  </div>

                  {/* Detail Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="card p-5">
                      <div className="flex items-center gap-2 text-surface-500 text-sm mb-2">
                        <Eye size={14} />
                        {t('dash.total_views')}
                      </div>
                      <div className="text-3xl font-extrabold text-surface-900">
                        {selectedSite.analytics.totalViews.toLocaleString()}
                      </div>
                      <div className="text-green-600 text-sm mt-1 flex items-center gap-1">
                        <TrendingUp size={12} />
                        +12% {t('dash.this_month')}
                      </div>
                    </div>
                    <div className="card p-5">
                      <div className="flex items-center gap-2 text-surface-500 text-sm mb-2">
                        <Users size={14} />
                        {t('dash.unique_visitors')}
                      </div>
                      <div className="text-3xl font-extrabold text-surface-900">
                        {selectedSite.analytics.uniqueVisitors.toLocaleString()}
                      </div>
                      <div className="text-green-600 text-sm mt-1 flex items-center gap-1">
                        <TrendingUp size={12} />
                        +8% {t('dash.this_month')}
                      </div>
                    </div>
                    <div className="card p-5">
                      <div className="flex items-center gap-2 text-surface-500 text-sm mb-2">
                        <Activity size={14} />
                        {t('dash.views_today')}
                      </div>
                      <div className="text-3xl font-extrabold text-surface-900">
                        {mockAnalyticsDaily[mockAnalyticsDaily.length - 1].views}
                      </div>
                      <div className="text-surface-400 text-sm mt-1">{t('dash.so_far')}</div>
                    </div>
                    <div className="card p-5">
                      <div className="flex items-center gap-2 text-surface-500 text-sm mb-2">
                        <Clock size={14} />
                        {t('dash.last_visit')}
                      </div>
                      <div className="text-lg font-bold text-surface-900">
                        {selectedSite.analytics.lastViewAt
                          ? new Date(selectedSite.analytics.lastViewAt).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </div>
                      <div className="text-surface-400 text-sm mt-1">{t('dash.today')}</div>
                    </div>
                  </div>

                  {/* Security Alert */}
                  {selectedSite.security.securityScore < 60 && (
                    <div className="card p-5 border-yellow-200 bg-yellow-50">
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold text-yellow-800 mb-1">{t('dash.low_security')}</div>
                          <p className="text-yellow-700 text-sm mb-3">
                            {t('dash.low_security_sub')} ({selectedSite.security.securityScore}/100)
                          </p>
                          <Link to="/security" className="btn bg-yellow-600 text-white btn-sm hover:bg-yellow-700">
                            <Shield size={14} />
                            {t('dash.improve_security')}
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {renewSiteId && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-auto"
          onClick={() => {
            setRenewSiteId(null);
            void reloadSites();
          }}
        >
          <div className="max-w-lg w-full mt-10" onClick={(e) => e.stopPropagation()}>
            <PublishPayment siteId={renewSiteId} mode="renew" onStatus={() => {}} />
            <button
              className="btn-outline btn-md mt-3 w-full"
              onClick={() => {
                setRenewSiteId(null);
                void reloadSites();
              }}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
